import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalController, ToastController } from '@ionic/angular';
import { ExternalEvaluationType } from 'src/app/external-evaluation/_interfaces/external-evaluation';
import { ExternalUserConfig } from 'src/app/external-evaluation/_interfaces/external-user-config';
import { BaseClass } from 'src/app/shared/classes/base';
import { ExternalUserCreationComponent } from 'src/app/shared/modals/external-user-creation/external-user-creation.component';
import { AuthService } from 'src/app/shared/services/auth.service';
import { ProcessConfig, ProcessExpirationResult } from '../../shared/interfaces/process-config.interface';
import { ProcessStatusEnum } from '../../shared/interfaces/process.interface';
import { TranslationService } from '../../shared/services/translation.service';
import { Evaluation360Service } from '../360-evaluation.service';
import { Evaluated360User } from '../_interfaces/360-evaluation-user.interface';
import { AreaConfig } from 'src/app/360-evaluation/_interfaces/area-config.interface';
import { RemoveExcessUsersModalComponent } from './remove-excess-users-modal/remove-excess-users-modal.component';
import { ThreeSixtyEvaluationStatus } from 'src/app/360-evaluation/_interfaces/360-evaluation-status';
import { ConfigService } from 'src/app/shared/services/multi-clients/config.service';

@Component({
    selector: 'app-360-evaluation',
    templateUrl: './evaluation.page.html',
    styleUrls: ['../360-evaluation.page.scss']
})
export class Evaluation360Page extends BaseClass {

    public allUsersCanDeleteThreeSixtyEval: boolean;
    public divide360UsersLitByAreaEnabled: boolean;

    public readonly _cycleId: number;
    public showThreeSixtyEvaluationLimit: boolean;

    public processConfig: ProcessConfig;
    public areas: AreaConfig[];
    public areaSelected: AreaConfig;
    public status: ProcessStatusEnum;
    public users: Array<Evaluated360User> = [];
    public allUsers: Array<Evaluated360User> = [];
    public isAllowedToReopen: boolean = false;
    public haveAccessToDelete: boolean = false;
    public haveAccessToChangeRelationship: boolean = false;
    public managerEvals: Array<Evaluated360User> = [];
    public ThreeSixtyEvaluationStatus = ThreeSixtyEvaluationStatus;
    public evaluatorCycleParticipantId: number;
    public areaIdSelected: number = null;

    private _userRoles: Array<string> = [];
    public isUserQuantityOk: boolean;
    public currentLanguage?: string;

    constructor(
            protected _toastController: ToastController,
            protected _translationService: TranslationService,
            private _authService: AuthService,
            private _router: Router,
            private _activatedRoute: ActivatedRoute,
            private _modalController: ModalController,
            private _evaluationService: Evaluation360Service,
            private _configService: ConfigService
    ) {
        super(_toastController, _translationService);
        const state = this._router.getCurrentNavigation().extras.state;
        this.currentLanguage = this._translationService.getSelectedLanguage();
        const accessByComponent = !state || !state.accessByComponent;
        if (accessByComponent) {
            this.notify(this._translationService
                    .getTranslationForCannotAccessByComponent(this.selectedLanguage), 'danger', 5000)
                    .then(() => history.back());
        }
        this._cycleId = parseInt(
                this._activatedRoute.snapshot.paramMap.get('cycleId'), 10
        );
        this._userRoles = this._authService.getUserRoles();
        if (this._userRoles.includes('Administrator') ||
                this._userRoles.includes('HR')) {
            this.isAllowedToReopen = true;
            this.haveAccessToDelete = true;
            this.haveAccessToChangeRelationship = true;
        }
        if (this.allUsersCanDeleteThreeSixtyEval) {
            this.haveAccessToDelete = true;
        }
        this.setEvironmentFeatures();
    }

    private setEvironmentFeatures() {
        this.allUsersCanDeleteThreeSixtyEval = this._configService.settings.features.allUsersCanDeleteThreeSixtyEval;
        this.divide360UsersLitByAreaEnabled = this._configService.settings.features.divide360UsersLitByArea;
        this.showThreeSixtyEvaluationLimit = this._configService.settings.features.showThreeSixtyEvaluationLimit;
        this.isUserQuantityOk = !this.showThreeSixtyEvaluationLimit;
    }

    async ionViewWillEnter() {
        await this.loadUsersFor360Evaluation();
    }


    public async loadUsersFor360Evaluation() {
        this._evaluationService.getApprovedUsersFor360Evaluation(
                this._cycleId
        ).subscribe((response: {data: {config:ProcessConfig,users,cycleParticipantId,status,processExpirationResult:ProcessExpirationResult}}) => {
            
            
            this.processConfig = response.data.config;
            this.users = response.data.users;
            this.allUsers = response.data.users;
            if (this.divide360UsersLitByAreaEnabled) {
                this.areas = this.processConfig.areas;
                if (this.areas) {
                    this.areaIdSelected = this.areaIdSelected == null ? this.areas[0].areaId : this.areaIdSelected;
                    this.areaSelected = this.areas.find(area => area.areaId === this.areaIdSelected);
                    this._updateUsers();
                }
            }
            this.evaluatorCycleParticipantId = response.data.cycleParticipantId;
            this.managerEvals = this.users.filter((user) => {
                return user.isManagerEvaluation
            });

            const processExpirationResult = response.data.processExpirationResult;
            var isAutomaticRemovalPerformed = processExpirationResult && processExpirationResult.isAutomaticRemovalPerformed;
            if (isAutomaticRemovalPerformed)
                this.notifyAutoRemoval(processExpirationResult);

            if(this.showThreeSixtyEvaluationLimit && !isAutomaticRemovalPerformed){
                this.verifyUserEvaluationInExcess(this.users);
            }

        }, (error) => this.notifyError(error));
    }

    private notifyAutoRemoval(processExpirationResult: ProcessExpirationResult) {
        
            const process = processExpirationResult;
            const evaluationLimit = this.processConfig.threeSixtyEvaluationLimit;
            const stepExpirationDate = process.stepExpirationDate ? new Date(process.stepExpirationDate) : new Date();

            const fallBackMsg = `Sua lista de avaliados excedeu o limite de {{evaluationLimit}} usuários. O prazo para ajuste encerrou em {{selectionEndDate}}, e o sistema removeu automaticamente {{excedentUsers}} usuários. Não é necessária nenhuma ação adicional. Prossiga com as avaliações.`;
            const msgCode = '360evaluation.evaluatorsLimit.autoremovalMessage';
            const messageRaw = this._translationService.getTranslation(msgCode) == msgCode ? fallBackMsg : this._translationService.getTranslation(msgCode);
            const message = messageRaw.replace('{{evaluationLimit}}', evaluationLimit.toString()).replace('{{selectionEndDate}}', stepExpirationDate.toLocaleDateString(this.currentLanguage)).replace('{{excedentUsers}}', process.refusedUsers.toString());
            this.notify(message, 'success', 15000);
    }

    public goBack(): void {
        this._router.navigate(['dashboard']);
    }

    public goToEvaluation(user: Evaluated360User): void {
        this._router.navigate([
            '360-evaluation/evaluation/' + this._cycleId + '/' + user.userId
        ]);
    }

    public changeTab(areaId: number) {
        this.areaIdSelected = areaId;
        this.areaSelected = this.areas.find(area => area.areaId === this.areaIdSelected);
        this._updateUsers();
    }

    private _updateUsers() {
        this.users = this.allUsers.filter((user) => {
            return (user.areaId === this.areaIdSelected)
        });
    }

    public async requestExternalEvaluation() {
        const user = this._authService.getImpersonatedUser();
        const externalUser: ExternalUserConfig = {
            cycleId: this._cycleId,
            evaluatedUserId: user.userId,
            evaluatedUserImageUrl: user.imageUrl,
            evaluatedUserName: user.username,
            cycleName: '',
            type: ExternalEvaluationType.ThreeSixtyEvaluation
        };
        const modal = await this._modalController.create({
            component: ExternalUserCreationComponent,
            cssClass: 'bigger-modal',
            componentProps: {
                externalUser: externalUser,
                isAdmin: false
            },
        });
        await modal.present();
    }

    public async verifyUserEvaluationInExcess(users: Evaluated360User[]){
        if(!this.processConfig || !this.processConfig.threeSixtyEvaluationLimit){
            console.log('No process config found', this.processConfig);
            return false;
        }
        const threeSixtyUsers = users.filter(u => u.isManagerEvaluation == false);
        const config = this.processConfig;
        const userLimit = config? config.threeSixtyEvaluationLimit : 1;
        
        if(threeSixtyUsers.length > userLimit){
            await this.createExcessUsersModal(config);
        }
    }

    public async createExcessUsersModal(config: ProcessConfig) {
        if(this.isUserQuantityOk){
            return;
        }
        const modal = await this._modalController.create({
            component: RemoveExcessUsersModalComponent,
            cssClass: 'extra-bigger-modal',
            componentProps: {
                'config': config,
                users: this.users,
                cycleId: this._cycleId,
            },
            backdropDismiss: false,
            id: 'removeExcessUsersModal'
        });
        await modal.present();

        modal.onDidDismiss().then(async (response) => {
            if (response.data && response.data.isUserQuantityOk) {
                await this.loadUsersFor360Evaluation();
            }else{
                this.goBack();
            }
            this.isUserQuantityOk = response.data ? response.data.isUserQuantityOk : false;
        });
    }
}
